<template>
  <div class="setup-page">
    <h1>Set up your Countdown</h1>

    <div v-if="loading" class="loading">Checking for an active countdown…</div>

    <div v-else>
      <p class="muted">
        Configure your default length and privacy. You can always change these later on the
        countdown page.
      </p>

      <div class="grid">
        <div class="field">
          <div class="field-grouping">
            <label for="length">
              Countdown length
              <computed-time :seconds="lengthSeconds" />
            </label>
            <div class="length-input">
              <input
                id="length"
                type="number"
                v-model.number="lengthSeconds"
                min="5"
                max="86400"
                step="5"
                placeholder="e.g., 120"
              />
              <span class="help">seconds</span>
            </div>
          </div>
          <p class="note">
            This is the time you have to cum, from when your your mistress/master/"friend" is
            starting the countdown, to you failing when it hits zero.
            <br />
            This can be changed later on the countdown page, too (if you are less confident in your
            abilities once a controller has joined…).
          </p>

          <hr />

          <div class="field-grouping">
            <label for="max-wait">
              Maximum wait (optional)
              <computed-time :seconds="maxWaitSeconds" />
            </label>
            <div class="length-input optional-input">
              <input
                id="random-wait-null"
                type="checkbox"
                :checked="maxWaitSeconds !== null"
                @click="maxWaitSeconds = maxWaitSeconds === null ? 7200 : null"
              />
              <input
                id="max-wait"
                type="number"
                v-model.number="maxWaitSeconds"
                min="0"
                max="86400"
                step="5"
                placeholder="e.g., 7200"
              />
              <span class="help">seconds</span>
            </div>
          </div>

          <div class="field-grouping">
            <label for="random-wait">
              Random auto-start window (optional)
              <computed-time :seconds="randomWaitSeconds" />
            </label>
            <div class="length-input optional-input">
              <input
                id="random-wait-null"
                type="checkbox"
                :checked="randomWaitSeconds !== null"
                @change="randomWaitSeconds = randomWaitSeconds === null ? 600 : null"
              />
              <input
                id="random-wait"
                type="number"
                v-model.number="randomWaitSeconds"
                min="0"
                max="86400"
                step="5"
                placeholder="e.g., 600"
              />
              <span class="help">seconds</span>
            </div>
          </div>
          <p class="note">
            After the maximum wait the system will automatically trigger a countdown at a random
            moment within set time window.
            <br />
            <br />
            With the current settings,
            <span v-if="maxWaitSeconds === null">
              the countdown will not start automatically after
            </span>
            <span v-else-if="randomWaitSeconds === null">
              the countdown will start automatically after
              <computed-time :seconds="maxWaitSeconds ?? 0" /> of
            </span>
            <span v-else>
              the countdown will start automatically sometime within
              <computed-time :seconds="maxWaitSeconds ?? 0" /> and
              <computed-time :seconds="(maxWaitSeconds ?? 0) + (randomWaitSeconds ?? 0)" /> after
            </span>
            starting the countdown session.
          </p>
          <p class="note">
            Leave these unset to require manual start of the countdown.
            <br />
            This can be changed later on the countdown page, too (if you realize your controller
            fell asleep).
          </p>
        </div>

        <div class="field">
          <label for="visibility">Visibility</label>
          <select id="visibility" v-model="form.visibility">
            <option value="link">Link only</option>
            <option value="friends">+ Friends (default)</option>
            <option value="public">&nbsp;&nbsp;+ Public</option>
          </select>
          <ul class="help-list">
            <li><strong>Link:</strong> anyone with your link can view/control.</li>
            <li>
              <strong>Friends:</strong> includes Link; people who used your link become friends;
              friends get notified.
            </li>
            <li><strong>Public:</strong> includes Friends; listed for everyone who’s logged in.</li>
          </ul>
        </div>

        <div class="field">
          <label>Your anonymity</label>
          <anon-settings v-model="ownerAnon" whose="my" />
          <p class="note">
            This can be changed later on the countdown page, too (if you realize you're not as bold
            as you thought).
          </p>
        </div>

        <div class="field">
          <label>Default for controllers</label>
          <anon-settings v-model="controllerDefaultAnon" whose="their" :user="AnonUser" />
          <p class="note">
            Controllers can override these defaults for themselves before joining and at any time
            within the session.
          </p>
        </div>
      </div>

      <div class="actions">
        <button class="primary" :disabled="creating" @click="createCountdown">
          {{ creating ? 'Creating…' : 'Create countdown' }}
        </button>
        <router-link class="link" to="/countdown">Go to countdown</router-link>
      </div>

      <p v-if="error" class="error">{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { apiFetch } from '@/api'
import { useAuthStore } from '@/stores/auth'
import { useToastStore } from '@/stores/toast'
import AnonSettings from '@/components/AnonSettings.vue'
import { AnonUser } from '@/components/AnonSettings.vue'
import ComputedTime from '@/components/ComputedTime.vue'

const router = useRouter()
const auth = useAuthStore()
const toasts = useToastStore()

const loading = ref(true)
const creating = ref(false)
const error = ref<string | null>(null)

const lengthSeconds = ref<number>(120)
function clampSeconds(n: number): number {
  return Math.max(0, Math.min(86400, Math.floor(n)))
}
watch(lengthSeconds, (v) => {
  if (v != null) lengthSeconds.value = clampSeconds(v)
})

// optional auto-start windows
const maxWaitSeconds = ref<number | null>(null)
const randomWaitSeconds = ref<number | null>(null)
function normalizeOptionalSeconds(v: number | null | undefined): number | null {
  if (v == null || Number.isNaN(v as number) || v < 0) return null
  return clampSeconds(v as number)
}
watch(maxWaitSeconds, (v) => {
  maxWaitSeconds.value = normalizeOptionalSeconds(v)
})
watch(randomWaitSeconds, (v) => {
  randomWaitSeconds.value = normalizeOptionalSeconds(v)
})

type Visibility = 'link' | 'friends' | 'public'
const form = ref({
  visibility: 'friends' as Visibility,
  owner_anon_username: false,
  owner_anon_name: false,
  owner_anon_photo: false,
  controller_default_anon_username: false,
  controller_default_anon_name: false,
  controller_default_anon_photo: false,
  max_wait_seconds: null as number | null,
  random_wait_seconds: null as number | null,
})

// Bridges for <anon-settings>
const ownerAnon = computed({
  get: () => ({
    anon_username: form.value.owner_anon_username,
    anon_name: form.value.owner_anon_name,
    anon_photo: form.value.owner_anon_photo,
  }),
  set: (v: { anon_username: boolean; anon_name: boolean; anon_photo: boolean }) => {
    form.value.owner_anon_username = v.anon_username
    form.value.owner_anon_name = v.anon_name
    form.value.owner_anon_photo = v.anon_photo
  },
})

const controllerDefaultAnon = computed({
  get: () => ({
    anon_username: form.value.controller_default_anon_username,
    anon_name: form.value.controller_default_anon_name,
    anon_photo: form.value.controller_default_anon_photo,
  }),
  set: (v: { anon_username: boolean; anon_name: boolean; anon_photo: boolean }) => {
    form.value.controller_default_anon_username = v.anon_username
    form.value.controller_default_anon_name = v.anon_name
    form.value.controller_default_anon_photo = v.anon_photo
  },
})

async function ensureNoActiveThenProceed() {
  try {
    const resp = await apiFetch('/api/me/active-session')
    if (resp.ok) {
      const session = (await resp.json()) as { id: number } | null
      if (session) {
        router.push('/countdown')
        return true
      }
    }
  } catch {
    // ignore; proceed to setup
  }
  return false
}

type ControlLinkResp = { uuid: string; url?: string }
type SessionOut = { id: number }

function extractDetail(x: unknown): string {
  if (typeof x === 'object' && x !== null && 'detail' in (x as Record<string, unknown>)) {
    const d = (x as { detail?: unknown }).detail
    return typeof d === 'string' ? `: ${d}` : ''
  }
  return ''
}

async function createCountdown() {
  if (!auth.user) {
    toasts.add('Please log in to create your countdown', 'info')
    router.push('/login')
    return
  }
  const seconds = clampSeconds(lengthSeconds.value)

  creating.value = true
  error.value = null
  try {
    // Create or get control link – this ensures a session exists in "created" state
    const linkResp = await apiFetch('/api/me/control-link', { method: 'POST' })
    if (!linkResp.ok) {
      const errPayload = await linkResp.json().catch(() => ({}) as unknown)
      const details = extractDetail(errPayload)
      throw new Error(`Failed to create control link${details}`)
    }
    const link = (await linkResp.json()) as ControlLinkResp

    // Resolve the session from the link UUID + apply session length
    const sessionResp = await apiFetch(`/api/link/${link.uuid}`, {
      method: 'POST',
      query: { seconds: seconds },
    })
    if (!sessionResp.ok) {
      const errPayload = await sessionResp.json().catch(() => ({}) as unknown)
      const details = extractDetail(errPayload)
      throw new Error(`Failed to resolve new session${details}`)
    }
    const session = (await sessionResp.json()) as SessionOut

    // Apply privacy via REST (also includes optional auto-start windows)
    form.value.max_wait_seconds = normalizeOptionalSeconds(maxWaitSeconds.value)
    form.value.random_wait_seconds = normalizeOptionalSeconds(randomWaitSeconds.value)
    const privacyResp = await apiFetch(`/api/sessions/${session.id}/privacy`, {
      method: 'PATCH',
      body: JSON.stringify({ ...form.value }),
    })
    if (!privacyResp.ok) {
      toasts.add('Could not save privacy preferences now', 'warning')
    }

    toasts.add('Countdown created', 'success')
    await router.push('/countdown')
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    error.value = msg
    toasts.add('Failed to create countdown', 'error', msg)
  } finally {
    creating.value = false
  }
}

onMounted(async () => {
  // Ensure auth
  if (!auth.user && auth.token) await auth.loadMe()
  if (!auth.user) {
    toasts.add('Please log in to set up your countdown', 'info')
    router.push('/login')
    return
  }
  // If user already has an active session, redirect to countdown view
  const redirected = await ensureNoActiveThenProceed()
  if (!redirected) loading.value = false
})
</script>

<style scoped lang="scss">
.setup-page {
  max-width: 720px;
  margin: 2rem auto;
  padding: 2rem;
  background: var(--color-background);
  color: var(--color-text);
  border-radius: 1rem;
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  h1 {
    margin-top: 0;
  }

  .muted {
    color: var(--color-text-muted);
  }

  .loading {
    opacity: 0.8;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1rem;
    margin-top: 1rem;
  }

  hr {
    margin: 1.5rem 0;
    border: none;
    border-top: 1px solid var(--color-border);
  }

  .field-grouping {
    margin-bottom: 1.5rem;
  }

  .field {
    border: 1px solid var(--color-border);
    border-radius: 0.6rem;
    padding: 0.75rem;
    background: var(--color-background-soft);
  }

  label {
    font-weight: 700;
    display: block;
    margin-bottom: 0.4rem;
    align-items: center;
    display: flex;
    gap: 0.5rem;
  }

  input,
  select {
    width: 100%;
    padding: 0.6rem;
    border-radius: 0.5rem;
    background: var(--color-background);
    color: var(--color-text);
    border: 1px solid var(--color-border);
  }

  .length-input {
    display: grid;
    align-items: center;
    gap: 0.5rem;
    grid-template-columns: 1fr max-content;
    &.optional-input {
      grid-template-columns: 1em 1fr max-content;
    }
  }

  .help {
    font-size: 0.85rem;
    opacity: 0.8;
  }

  .help-list {
    margin: 0.5rem 0 0;
    padding-left: 1rem;
    opacity: 0.85;
  }

  .toggles {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .note {
    margin-top: 0.35rem;
    font-size: 0.85rem;
    color: var(--color-text-muted);
  }

  .actions {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    margin-top: 1.25rem;
  }

  .primary {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: #fff;
    border: none;
    padding: 0.7rem 1rem;
    border-radius: 0.6rem;
    cursor: pointer;
  }

  .link {
    color: var(--color-text);
    opacity: 0.8;
    text-decoration: underline;
  }

  .error {
    color: #c0392b;
    margin-top: 1rem;
  }
  button {
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}
</style>
